// src/App.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HeaderMainPage from '../../header/HeaderMainPage';
import Swal from 'sweetalert2';
import { FaQuestionCircle } from 'react-icons/fa';

const InformationContainer = () => {
  /* 
  * This component is responsible for displaying the form where users can input their campaign information.
  * It includes fields for Core Product, Benefit, Target Audience, Ideal Client, Unique Selling Point, etc.
  */
  const navigate = useNavigate();
  const [CoreProduct, setCoreProduct] = useState('');
  const [Benefit, setBenefit] = useState([]);
  const [currentBenefit, setCurrentBenefit] = useState('');  //  for Temporary input for benefits
  const [TargetAudience, setTargetAudience] = useState('');
  const [GeoLocations, setGeoLocations] = useState(''); // NEW: geographical locations
  const [IdealClient, setIdealClient] = useState([]);
  const [CurrentIdealClient, setCurrentIdealClient] = useState('')  // for temporary input for ideal clent
  const [UniqueSellingPoint, setUniqueSellingPoint] = useState('');
  const roboman_api = process.env.REACT_APP_ROBOMAN_API;
  const [numberOfCustomers, setNumberOfCustomers] = useState(250);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [BuyingIntent, setBuyingIntent] = useState(false); // buying intent boolean

  // Credits-per-prospect + estimated credits
  const creditsPerProspect = BuyingIntent ? 2.5 : 0.5;
  const estimatedCredits = numberOfCustomers * creditsPerProspect;
  const formattedEstimatedCredits =
    Number.isNaN(estimatedCredits)
      ? 0
      : (estimatedCredits % 1 === 0
        ? estimatedCredits.toString()
        : estimatedCredits.toFixed(1));

  const updateNumberOfCustomers = (value) => {
    setNumberOfCustomers(value);
    localStorage.setItem('numberOfCustomers', value);
  };

  const toggleBuyingIntent = (value) => {
    setBuyingIntent(value);
    localStorage.setItem('buying_intent', value ? 'true' : 'false');
  };

  const extractCampaignInfo = async (text) => {
    /*
    * This function sends the input text to the server for parsing and extracts campaign information.
    * It updates the local storage with the parsed data.
    * Returns the parsed data.
    */



    try {
      const response = await fetch(`${roboman_api}/llm/campaign/parser`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
        },
        body: JSON.stringify({
          input_str: JSON.stringify(text)
        }),
      });

      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
      }


      const data = await response.json();

      if (data) {
        console.log('parsing data successfull');
        localStorage.setItem('campaign_name', data.campaign_name);
        localStorage.setItem('core_service', data.core_service);
        localStorage.setItem('unique_selling_proposition', data.unique_selling_proposition); // must be unique_selling_point
        localStorage.setItem('target_audience', data.target_audience);
        localStorage.setItem('problem_solved', data.problem_solved);
        localStorage.setItem('key_benefits', JSON.stringify(data.key_benefits));
        localStorage.setItem('primary_goal_of_outreach_campaign', data.primary_goal_of_outreach_campaign);
        localStorage.setItem('ideal_client', JSON.stringify(data.ideal_client));
        localStorage.setItem('success_measurement', data.success_measurement);
        localStorage.setItem('suggest_usp', data.suggest_unique_selling_point); // must be suggest_unique_selling_point
      }

      return data;
    } catch (error) {
      console.error('Error extracting campaign info:', error);
    }
  };

  useEffect(() => {
    // Load values from localStorage or set to empty string if not found
    setCoreProduct(localStorage.getItem('core_service') || '');
    setBenefit(localStorage.getItem('benefit') || '');
    setTargetAudience(localStorage.getItem('target_audience') || '');
    setGeoLocations(localStorage.getItem('geo_locations') || ''); // NEW: load geo locations
    setIdealClient(localStorage.getItem('ideal_client_input') || '');
    setUniqueSellingPoint(localStorage.getItem('unique_selling_proposition') || '');
    setNumberOfCustomers(Number(localStorage.getItem('numberOfCustomers')) || 250);


    //*******

    //for benefits
    const storedBenefits = localStorage.getItem('benefit');
    if (storedBenefits) {
      try {
        const parsed = JSON.parse(storedBenefits);
        setBenefit(Array.isArray(parsed) ? parsed : []);
      } catch {
        setBenefit([]);
      }
    }
    // for ideal clients
    const storedIdealClients = localStorage.getItem('ideal_client_input');
    if (storedIdealClients) {
      try {
        const parsed = JSON.parse(storedIdealClients);
        setBenefit(Array.isArray(parsed) ? parsed : []);
      } catch {
        setIdealClient([]);
      }
    }

    //******




    const storedBuyingIntent = localStorage.getItem('buying_intent'); // load buying intent
    if (storedBuyingIntent !== null) {
      setBuyingIntent(storedBuyingIntent === 'true');
    }
  }, []);

  const handleExit = () => {
    navigate('/campinitselect');
  };

  const handleNext = async () => {
    /*
    * This function handles the "Next" button click event.
    * It checks if all fields are filled, saves the data to localStorage,
    * and calls the extractCampaignInfo function to parse the data.
    * Finally, it navigates to the next page.
    */

    if (!CoreProduct || !Benefit || !TargetAudience || !IdealClient || !UniqueSellingPoint) {
      Swal.fire({
        title: 'Missing Information',
        text: 'Please fill in all fields before proceeding.',
        icon: 'warning',
        confirmButtonText: 'Close',
        confirmButtonColor: "#223F9E"
      });
    } else {
      // Save all fields to localStorage
      localStorage.setItem('core_service', CoreProduct);
      localStorage.setItem('benefit', JSON.stringify(Benefit));
      localStorage.setItem('target_audience', TargetAudience);
      localStorage.setItem('geo_locations', GeoLocations); // NEW: save geo locations
      localStorage.setItem('ideal_client_input', IdealClient);
      localStorage.setItem('unique_selling_proposition', UniqueSellingPoint);
      localStorage.setItem('buying_intent', BuyingIntent ? 'true' : 'false'); // persist on Next as well

      //****** 
      // Format benefits for parser
      const benefitsText = Benefit.length > 0
        ? Benefit.map((b, i) => `${i + 1}. ${b}`).join('\n')
        : '';

      const IdealClientText = IdealClient.length > 0
        ? IdealClient.map((b, i) => `${i + 1}. ${b}`).join('\n')
        : '';

      //******

      const parser_input = `Here are user information:
      **Core Service/Product**: ${CoreProduct}
      **Target Audience**: ${TargetAudience}
      **Geographical Locations**: ${GeoLocations}
      **Ideal Client**: ${IdealClient}
      **Key Benefits**: ${Benefit}
      **Unique Selling Proposition**: ${UniqueSellingPoint}`;

      // Show a loading alert
      Swal.fire({
        title: 'Please wait...',
        text: 'Extracting campaign information.',
        icon: 'info',
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading(); // Show loading spinner
        }
      });

      // Call the async function and wait for it to complete
      await extractCampaignInfo(parser_input);

      // Close the loading alert
      Swal.close();

      // navigate('/onboardingcampmatch');
      navigate('/reviewinfo');
    }
  };

  // Update state setters to also save to localStorage
  const updateName = (value) => {
    // This function updates the campaign name and saves it to localStorage
    setName(value);
    localStorage.setItem('campaign_name', value);
  };

  const updateDescription = (value) => {
    // This function updates the description and saves it to localStorage
    setDescription(value);
    localStorage.setItem('campaignDescription', value);
  };


  //*********  
  // to hadle benefits on add button 
  function handleAddBenefit() {
    if (currentBenefit.trim()) {
      setBenefit([...Benefit, currentBenefit.trim()]);
      setCurrentBenefit(""); // Clear input after adding
    }
  }
  // to hadle benefits on delete button 
  function handleDeleteBenefit(index) {
    setBenefit(Benefit.filter((_, i) => i !== index));
  }


  // handle ideal clients on add button
  function handleAddIdealClient() {
    if (CurrentIdealClient.trim()) {
      setIdealClient([...IdealClient, CurrentIdealClient.trim()]);
      setCurrentIdealClient(""); // Clear input after adding
    }
  }
  // to hadle benefits on delete button 
  function handleDeleteIdealClient(index) {
    setIdealClient(IdealClient.filter((_, i) => i !== index));
  }

  const IconWithPopup = ({ message }) => {
    // This component displays an icon with a tooltip that shows a message when hovered over.
    const [isHovered, setIsHovered] = useState(false);



    return (
      <div
        className="relative inline-block"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="text-lg cursor-pointer ml-1 text-slate-400 hover:text-indigo-500 transition-colors">
          <FaQuestionCircle />
        </div>
        {isHovered && (
          <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-[300px] p-3 bg-slate-900 text-white text-xs rounded-xl shadow-2xl z-10 border border-slate-700">
            {message}
          </div>
        )}
      </div>
    );
  };

  const truncate = (text, limit = 80) => {
    if (!text) return '';
    return text.length > limit ? text.slice(0, limit) + '…' : text;
  };

  return (
    <div className="relative overflow-hidden bg-white/90 backdrop-blur-xl rounded-2xl border border-slate-200 shadow-[0_18px_45px_rgba(15,23,42,0.25)]">
      {/* Soft gradient glows on the card itself */}
      <div className="pointer-events-none absolute -top-24 -right-28 h-56 w-56 rounded-full bg-gradient-to-br from-indigo-500/15 via-sky-500/10 to-emerald-400/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-64 w-64 rounded-full bg-gradient-to-tr from-indigo-500/15 via-sky-500/10 to-violet-500/10 blur-3xl" />

      <div className="relative z-10">
        {/* Top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-[#223F9E] via-indigo-600 to-violet-600" />

        {/* Header */}
        <div className="px-6 py-4 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold font-Inter opacity-1 bg-gradient-to-r from-gray-900 via-blue-800 to-fuchsia-800  bg-clip-text text-transparent ">
              Your Campaign Blueprint<span className="inline-flex items-center rounded-full bg-slate-900 text-slate-50 text-[11px] px-2.5 py-0.5 mx-5">
                Prospects: {numberOfCustomers}
              </span>
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 max-w-xl">
              Tell Roboman what you sell and who you want to reach. We&apos;ll turn this into targeting, messaging and outreach steps.
            </p>
          </div>

          <button
            onClick={handleExit}
            type="button"
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 hover:border-slate-300 hover:shadow-sm transition-all"
          >
            <span className="mr-1.5 text-base leading-none">&times;</span>
            Exit
          </button>
        </div>

        {/* Body: form + live summary + aligned prospects/credits row */}
        <div className="px-4 sm:px-6 pt-4 pb-6 grid gap-6
                grid-cols-1
                lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          {/* FORM SIDE (without number-of-prospects) */}
          <form className="space-y-4">
            {/* Name */}
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-3.5 hover:border-indigo-300 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300 transition-all">
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="name"
                  className="block text-[11px] font-semibold tracking-wide uppercase text-slate-600"
                >
                  Campaign name
                </label>
                <span className="text-[10px] text-slate-400">Internal only</span>
              </div>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => updateName(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="e.g. UK SaaS Founders – Demo Booking Sprint"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                Give this campaign a name that helps you recognise the segment or offer.
              </p>
            </div>

            {/* Description */}
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-3.5 hover:border-indigo-300 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300 transition-all">
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="description"
                  className="block text-[11px] font-semibold tracking-wide uppercase text-slate-600"
                >
                  Campaign description
                </label>
                <span className="text-[10px] text-slate-400">Optional</span>
              </div>
              <textarea
                id="description"
                value={description}
                onChange={(e) => updateDescription(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent h-20 resize-none"
                placeholder="Short context about the offer, channel mix or who this campaign is for."
              />
            </div>

            {/* Core Product */}
            <div className="rounded-xl border border-slate-200/80 bg-white p-3.5 hover:border-indigo-300 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300 transition-all">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center">
                  <label
                    htmlFor="core product"
                    className="block text-[11px] font-semibold tracking-wide uppercase text-slate-600"
                  >
                    Core product / service
                  </label>
                  <IconWithPopup message={"Please input the name of Core Product with a short description."} />
                </div>
                <span className="text-[10px] text-rose-500 font-medium">Required</span>
              </div>
              <textarea
                type="text"
                id="core product"
                value={CoreProduct}
                onChange={(e) => setCoreProduct(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent h-20 resize-none"
                placeholder="What are you selling? Add 1–2 lines so the AI understands it clearly."
              />
            </div>

            {/* Benefit */}
            <div className="rounded-xl border border-slate-200/80 bg-white p-3.5 hover:border-indigo-300 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300 transition-all">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center">
                  <label
                    htmlFor="benefit"
                    className="block text-[11px] font-semibold tracking-wide uppercase text-slate-600"
                  >
                    Key benefits
                  </label>
                  <IconWithPopup message={"Please input the benefits of your product."} />
                </div>
                <span className="text-[10px] text-slate-400">Value & outcomes</span>
              </div>


              {/* Display list of added benefits */}
              {Benefit.length > 0 && (
                <div className="mb-3 space-y-2">
                  {Benefit.map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-wrap items-start gap-2 p-2.5 bg-indigo-50/80 rounded-lg border border-indigo-100/50"
                    >
                      <span className="text-indigo-600 mt-0.5 text-sm">•</span>
                      <span className="flex-1 text-sm text-slate-700">{item}</span>
                      <button
                        onClick={() => handleDeleteBenefit(index)}
                        className="text-slate-400 hover:text-red-500 transition-colors text-lg leading-none"
                        type="button"
                        title="Remove benefit"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Input area */}
              <div className="flex gap-2">
                <textarea
                  type="text"
                  id="benefit"
                  value={currentBenefit}
                  onChange={(e) => setCurrentBenefit(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddBenefit();
                    }
                  }}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent h-20 resize-none"
                  placeholder="What changes for your client after working with you? Add 2–4 bullet style lines."
                />
                <button
                  onClick={handleAddBenefit}
                  type="button"
                  className="px-4 py-2 bg-gradient-to-r from-[#223F9E] via-indigo-600 to-violet-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors h-fit shadow-sm"
                >
                  Add
                </button>
              </div>
            </div>
            {/* Input area */}
            {/* <textarea
              type="text"
              id="benefit"
              value={Benefit}
              onChange={(e) => setBenefit(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent h-20 resize-none"
              placeholder="What changes for your client after working with you? Add 2–4 bullet style lines."
            /> */}



            {/* Target Audience */}
            <div className="rounded-xl border border-slate-200/80 bg-white p-3.5 hover:border-indigo-300 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300 transition-all">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center">
                  <label
                    htmlFor="target audience"
                    className="block text-[11px] font-semibold tracking-wide uppercase text-slate-600"
                  >
                    Target audience
                  </label>
                  <IconWithPopup message={"Please input your target audience including your target industry and the region for your product."} />
                </div>
                <span className="text-[10px] text-slate-400">Who you want to reach</span>
              </div>
              <textarea
                type="text"
                id="target audience"
                value={TargetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent h-20 resize-none"
                placeholder="e.g. Founders & CEOs of B2B SaaS companies in the UK & EU, 10–200 employees."
              />
            </div>

            {/* NEW: Geographical locations */}
            <div className="rounded-xl border border-slate-200/80 bg-white p-3.5 hover:border-indigo-300 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300 transition-all">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center">
                  <label
                    htmlFor="geo-locations"
                    className="block text-[11px] font-semibold tracking-wide uppercase text-slate-600"
                  >
                    Geographical locations
                  </label>
                  <IconWithPopup message={"Add one or multiple locations where you want to target prospects. You can separate them with commas or line breaks."} />
                </div>
                <span className="text-[10px] text-slate-400">Regions you want to target</span>
              </div>
              <textarea
                id="geo-locations"
                value={GeoLocations}
                onChange={(e) => setGeoLocations(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent h-20 resize-none"
                placeholder="e.g. London (UK), Berlin (Germany), Dubai (UAE), Remote-friendly across EU."
              />
            </div>

            {/* Ideal Client */}

            {/* Display list of added benefits */}
            {IdealClient.length > 0 && (
              <div className="mb-3 space-y-2">
                {IdealClient.map((item, index) => (
                  <div
                    key={index}
                    className="flex flex-wrap items-start gap-2 p-2.5 bg-indigo-50/80 rounded-lg border border-indigo-100/50"
                  >
                    <span className="text-indigo-600 mt-0.5 text-sm">•</span>
                    <span className="flex-1 text-sm text-slate-700">{item}</span>
                    <button
                      onClick={() => handleDeleteIdealClient(index)}
                      className="text-slate-400 hover:text-red-500 transition-colors text-lg leading-none"
                      type="button"
                      title="Remove benefit"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="rounded-xl border border-slate-200/80 bg-white p-3.5 hover:border-indigo-300 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300 transition-all">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center">
                  <label
                    htmlFor="ideal client"
                    className="block text-[11px] font-semibold tracking-wide uppercase text-slate-600"
                  >
                    Ideal client profile
                  </label>
                  <IconWithPopup message={"Please input your ideal client that your product is suitable for. The information can include age, working position, economic situation, etc."} />
                </div>
                <span className="text-[10px] text-slate-400">ICP details</span>
              </div>


              <div className="flex gap-2">
                <textarea
                  type="text"
                  id="ideal client"
                  value={CurrentIdealClient}
                  onChange={(e) => setCurrentIdealClient(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddBenefit();
                    }
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent h-20 resize-none"
                  placeholder="Role, company size, budget, tech stack, pain points, etc."
                />
                <button
                  onClick={handleAddIdealClient}
                  type="button"
                  className="px-4 py-2 bg-gradient-to-r from-[#223F9E] via-indigo-600 to-violet-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors h-fit shadow-sm"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Unique Selling Point */}
            <div className="rounded-xl border border-slate-200/80 bg-white p-3.5 hover:border-indigo-300 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300 transition-all">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center">
                  <label
                    htmlFor="unique selling point"
                    className="block text-[11px] font-semibold tracking-wide uppercase text-slate-600"
                  >
                    Unique selling point
                  </label>
                  <IconWithPopup message={"Please provide your unique selling point of your product which will differentiate your product from competitors in the market."} />
                </div>
                <span className="text-[10px] text-slate-400">Why you?</span>
              </div>
              <textarea
                type="text"
                id="unique selling point"
                value={UniqueSellingPoint}
                onChange={(e) => setUniqueSellingPoint(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent h-20 resize-none"
                placeholder="What do you do differently / better than alternatives?"
              />
            </div>

            {/* Buying Intent */}
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/80 p-3.5 hover:border-indigo-300 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300 transition-all">
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="buying-intent"
                  className="block text-[11px] font-semibold tracking-wide uppercase text-slate-600"
                >
                  Buying intent
                </label>
                <span className="text-[10px] text-slate-400">Boolean</span>
              </div>
              <p className="text-[11px] text-slate-500 mb-2">
                Using this you would get prospects with their intent scores, so you can prioritise the autopilot strategy.
              </p>
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => toggleBuyingIntent(false)}
                  className={
                    'px-3 py-1.5 rounded-full transition-all ' +
                    (!BuyingIntent
                      ? 'bg-slate-900 text-slate-50 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800')
                  }
                >
                  Off
                </button>
                <button
                  type="button"
                  onClick={() => toggleBuyingIntent(true)}
                  className={
                    'px-3 py-1.5 rounded-full transition-all ' +
                    (BuyingIntent
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-800')
                  }
                >
                  On
                </button>
              </div>
            </div>
          </form>

          {/* LIVE SUMMARY SIDE (right column main box) */}
          <aside className="hidden lg:flex flex-col rounded-2xl border border-slate-900/40 bg-slate-950 text-slate-50 shadow-inner px-4 py-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400">
                  Live campaign preview
                </p>
                <h3 className="mt-1 text-sm font-semibold">
                  {name || 'Untitled campaign'}
                </h3>
              </div>
              <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-300 text-[11px] px-3 py-1">
                {numberOfCustomers} prospects
              </span>
            </div>

            <div className="border-t border-slate-800 pt-3 space-y-3 text-xs">
              <div>
                <p className="text-slate-400 mb-0.5">Core service</p>
                <p className="text-slate-100">
                  {CoreProduct ? truncate(CoreProduct, 90) : 'Describe your core offer to see it here.'}
                </p>
              </div>
              <div>
                <p className="text-slate-400 mb-0.5">Target audience</p>
                <p className="text-slate-100">
                  {TargetAudience ? truncate(TargetAudience, 90) : 'Who do you want Roboman to reach out to?'}
                </p>
              </div>
              <div>
                <p className="text-slate-400 mb-0.5">Geographical locations</p>
                <p className="text-slate-100">
                  {GeoLocations ? truncate(GeoLocations, 90) : 'Add locations to focus your prospecting on specific regions.'}
                </p>
              </div>
              <div>
                <p className="text-slate-400 mb-0.5">Ideal client</p>
                <p className="text-slate-100">
                  {IdealClient ? truncate(IdealClient, 90) : 'Add ICP details to help qualify prospects better.'}
                </p>
              </div>
              <div>
                <p className="text-slate-400 mb-0.5">Unique selling point</p>
                <p className="text-slate-100">
                  {UniqueSellingPoint ? truncate(UniqueSellingPoint, 90) : 'Your USP will inform hooks and angles in your outreach.'}
                </p>
              </div>
              <div>
                <p className="text-slate-400 mb-0.5">Buying intent</p>
                <p className="text-slate-100">
                  {BuyingIntent
                    ? 'Enabled – we will pull prospects with intent scores.'
                    : 'Disabled – standard prospect selection only.'}
                </p>
              </div>
            </div>

            <div className="mt-auto rounded-xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-950 px-3 py-3 text-[11px] text-slate-300">
              <p className="font-medium text-slate-100 mb-0.5">
                What happens next?
              </p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>We parse this info into structured campaign metadata.</li>
                <li>Roboman suggests targeting, messaging and angles.</li>
                <li>You&apos;ll review it before activating outreach.</li>
              </ul>
            </div>
          </aside>

          {/* ROW 2: Number of prospects (left) + Credits usage (right) */}
          <div className="mt-2 lg:col-start-1 lg:row-start-2">
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/80 p-3.5 hover:border-indigo-300 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300 transition-all">
              <label
                htmlFor="numberOfCustomers"
                className="block text-[11px] font-semibold tracking-wide uppercase text-slate-600 mb-1.5"
              >
                Number of prospects
              </label>

              <div className="flex flex-col gap-3">
                {/* Range + number input */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <input
                    type="number"
                    id="numberOfCustomers"
                    min="1"
                    max="450"
                    value={numberOfCustomers}
                    onChange={(e) => updateNumberOfCustomers(Number(e.target.value))}
                    className="w-24 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <div className="flex-1">
                    <input
                      type="range"
                      min="1"
                      max="450"
                      value={numberOfCustomers}
                      onChange={(e) => updateNumberOfCustomers(Number(e.target.value))}
                      className="w-full accent-[#223F9E]"
                    />
                  </div>
                </div>

                {/* Preset chips */}
                <div className="flex flex-wrap items-center gap-2">
                  {[10, 100, 250].map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => updateNumberOfCustomers(val)}
                      className={
                        `px-4 py-1.5 rounded-full border text-xs font-medium transition-all ` +
                        (numberOfCustomers === val
                          ? 'bg-[#223F9E] text-white border-transparent shadow-sm'
                          : 'bg-slate-100 text-slate-700 border-transparent hover:border-slate-300 hover:bg-slate-50')
                      }
                    >
                      {val}
                    </button>
                  ))}
                  <span className="text-[11px] text-slate-400">
                    Roboman will cap outreach for this campaign to this number.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-2 lg:col-start-2 lg:row-start-2">
            <div className="rounded-2xl border border-slate-900/40 bg-slate-950 text-slate-50 shadow-inner px-4 py-3 text-xs text-emerald-50 shadow-inner">
              <p className="text-[11px] uppercase tracking-wide text-emerald-300 mb-1">
                Estimated credits usage
              </p>
              {numberOfCustomers ? (
                <>
                  <p className="text-sm font-semibold">
                    {formattedEstimatedCredits} credits
                  </p>
                  <p className="text-[11px] text-emerald-100 mt-0.5">
                    {numberOfCustomers} prospects × {creditsPerProspect} credits per prospect.
                  </p>
                  <p className="text-[11px] text-emerald-200 mt-1">
                    Intent {BuyingIntent ? 'enabled (2.5 credits / prospect)' : 'disabled (0.5 credits / prospect)'}.
                  </p>
                </>
              ) : (
                <p className="text-[11px] text-emerald-100">
                  Set a prospect volume to see how many credits this campaign will use.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer / Next */}
        <div className="w-full sm:w-auto inline-flex items-center justify-between gap-3 px-6 pb-5 pt-3 border-t border-slate-200 bg-slate-50/70">
          <p className="text-[11px] text-slate-500 max-w-sm">
            Make sure each field is filled in. The better quality here, the sharper your campaigns & replies will be.
          </p>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#223F9E] via-indigo-600 to-violet-600 px-7 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all"
            onClick={handleNext}
          >
            Next
            <span className="ml-2 text-base leading-none">→</span>
          </button>
        </div>
      </div >
    </div >
  );
};

const CampManualFill = () => {
  // Main UI for Manual Fill for Campaign Creation
  // Background kept as earlier
  return (
    <div className="bg-gray-100 min-h-screen flex flex-col items-center">
      <HeaderMainPage />
      <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8 my-5 mt-6">
        <InformationContainer />
      </div>
    </div>
  );
};

//Rebuild test

export default CampManualFill;
